/**
 * Cine playback controller — advances a `setIndex` on an interval
 * derived from the requested frame rate. Designed to be dropped into
 * a SolidJS component with createEffect + onCleanup.
 */
import { createSignal } from "solid-js";

export interface CineController {
  playing: () => boolean;
  fps: () => number;
  setFps: (n: number) => void;
  toggle: (setter: (fn: (i: number) => number) => void, bounds: () => number) => void;
  stop: () => void;
}

export function createCine(defaultFps = 15): CineController {
  const [playing, setPlaying] = createSignal(false);
  const [fps, setFps] = createSignal(defaultFps);
  let handle: ReturnType<typeof setInterval> | null = null;

  const stop = () => {
    if (handle) { clearInterval(handle); handle = null; }
    setPlaying(false);
  };

  const toggle = (setter: (fn: (i: number) => number) => void, bounds: () => number) => {
    if (playing()) {
      stop();
      return;
    }
    setPlaying(true);
    const tick = () => {
      setter((i) => {
        const n = bounds();
        if (n <= 0) return i;
        return (i + 1) % n;
      });
    };
    const ms = Math.max(33, Math.round(1000 / Math.max(1, fps())));
    handle = setInterval(tick, ms);
  };

  return { playing, fps, setFps, toggle, stop };
}
